import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  HeartHandshake, Pencil, Loader2, Save, MapPin, Globe, Phone, Mail, ExternalLink, Search, Plus,
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BLANK = {
  name: "",
  description: "",
  website: "",
  phone: "",
  email: "",
  address: "",
  zip_codes_text: "", // comma-separated string form for the input
  is_active: true,
};

export default function CommunityPartnersAdmin() {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // { id, ...form }
  const [saving, setSaving] = useState(false);

  // Nearby-zips lookup state (per-open-modal)
  const [zipLookup, setZipLookup] = useState({ query: "", results: null, loading: false });

  const resetLookup = () => setZipLookup({ query: "", results: null, loading: false });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/community-partners`);
      setPartners(data);
    } catch {
      toast.error("Failed to load Community Partners");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openEdit = (p) => {
    resetLookup();
    setEditing({
      id: p.id,
      name: p.name || "",
      description: p.description || "",
      website: p.website || "",
      phone: p.phone || "",
      email: p.email || "",
      address: p.address || "",
      zip_codes_text: (p.zip_codes || []).join(", "),
      is_active: p.is_active !== false,
    });
  };

  const closeEdit = () => { setEditing(null); resetLookup(); };

  // ---- Zip lookup helpers ----
  const parseCityState = (raw) => {
    // Accept "Charlotte, NC" or "Charlotte NC" or just "Charlotte"
    const trimmed = (raw || "").trim();
    if (!trimmed) return { city: "", state: null };
    // Comma format
    if (trimmed.includes(",")) {
      const [c, s] = trimmed.split(",").map((x) => x.trim());
      return { city: c, state: s || null };
    }
    // "City ST" (2-letter state code at end)
    const m = trimmed.match(/^(.+?)\s+([A-Za-z]{2})$/);
    if (m) return { city: m[1].trim(), state: m[2].trim() };
    return { city: trimmed, state: null };
  };

  const runZipLookup = async () => {
    const { city, state } = parseCityState(zipLookup.query);
    if (!city) {
      toast.error("Enter a city — e.g. \"Charlotte, NC\"");
      return;
    }
    setZipLookup((s) => ({ ...s, loading: true, results: null }));
    try {
      const params = { city };
      if (state) params.state = state;
      const { data } = await axios.get(`${API}/admin/geo/zips`, { params });
      setZipLookup({ query: zipLookup.query, loading: false, results: data });
    } catch (e) {
      setZipLookup({ query: zipLookup.query, loading: false, results: null });
      toast.error("Lookup failed", { description: e.response?.data?.detail || "Try adding a state, e.g. \"Rock Hill, SC\"" });
    }
  };

  const addZipsToForm = (zipsToAdd) => {
    if (!editing) return;
    const existing = editing.zip_codes_text.match(/\b\d{5}\b/g) || [];
    const merged = Array.from(new Set([...existing, ...zipsToAdd]));
    setEditing({ ...editing, zip_codes_text: merged.join(", ") });
    toast.success(`Added ${zipsToAdd.length} zip${zipsToAdd.length === 1 ? "" : "s"}`, {
      description: `${merged.length} total in the list now`,
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        name: editing.name,
        description: editing.description,
        website: editing.website,
        phone: editing.phone,
        email: editing.email,
        address: editing.address,
        zip_codes: editing.zip_codes_text, // backend regex-parses free-text
        is_active: editing.is_active,
      };
      const { data } = await axios.put(`${API}/admin/community-partners/${editing.id}`, payload);
      const parsedZips = (data.zip_codes || []).length;
      toast.success("Community Partner updated", {
        description: parsedZips
          ? `${parsedZips} zip code${parsedZips === 1 ? "" : "s"} saved`
          : "No valid 5-digit zips detected — partner won't appear in searches",
      });
      await load();
      closeEdit();
    } catch (e) {
      toast.error("Save failed", { description: e.response?.data?.detail || "Please try again" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <section data-testid="admin-community-partners-section" className="bg-white rounded-2xl shadow-sm border border-[#E2E8F0] p-6 md:p-8">
      <div className="flex items-start gap-3 mb-2">
        <HeartHandshake className="w-6 h-6 text-secondary flex-shrink-0 mt-0.5" />
        <div>
          <h2 className="text-2xl font-heading font-semibold text-accent">Approved Community Partners</h2>
          <p className="text-sm text-text-secondary font-body mt-1">
            Edit an approved partner's zip codes, website, contact info, and listing status. Zip codes control where they appear when families search.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </div>
      ) : partners.length === 0 ? (
        <p className="text-text-secondary font-body italic mt-6">No approved Community Partners yet. Approve inquiries above to add them here.</p>
      ) : (
        <div className="mt-5 space-y-3">
          {partners.map((p) => (
            <div
              key={p.id}
              data-testid={`partner-row-${p.id}`}
              className={`flex flex-wrap items-start gap-4 p-4 rounded-xl border border-[#E2E8F0] bg-[#FAF9F6] ${!p.is_active ? "opacity-60" : ""}`}
            >
              <div className="flex-1 min-w-[240px]">
                <div className="flex items-center flex-wrap gap-2">
                  <p className="font-body font-bold text-text-primary">{p.name}</p>
                  {!p.is_active && (
                    <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-gray-200 text-gray-700">
                      Hidden
                    </span>
                  )}
                  <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-secondary/15 text-secondary">
                    Community Partner
                  </span>
                </div>
                <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-xs font-body text-text-secondary">
                  {p.phone && (
                    <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" /> {p.phone}</span>
                  )}
                  {p.email && (
                    <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" /> {p.email}</span>
                  )}
                  {p.website && (
                    <a href={p.website.startsWith("http") ? p.website : `https://${p.website}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-primary hover:underline">
                      <Globe className="w-3 h-3" /> {p.website} <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
                <div className="mt-2 flex items-start gap-1.5 text-xs font-body text-text-secondary">
                  <MapPin className="w-3.5 h-3.5 text-primary flex-shrink-0 mt-0.5" />
                  <span>
                    {(p.zip_codes || []).length > 0 ? (
                      <><strong className="text-text-primary">{p.zip_codes.length} zip{p.zip_codes.length === 1 ? "" : "s"}:</strong> {p.zip_codes.join(", ")}</>
                    ) : (
                      <span className="text-amber-700 font-semibold">⚠️ No zip codes — won't appear in searches. Edit to add.</span>
                    )}
                  </span>
                </div>
              </div>
              <Button
                data-testid={`partner-edit-${p.id}`}
                onClick={() => openEdit(p)}
                variant="outline"
                size="sm"
                className="border-2 border-primary text-primary hover:bg-primary/5 rounded-full font-body font-bold flex-shrink-0"
              >
                <Pencil className="w-3.5 h-3.5 mr-1.5" /> Edit
              </Button>
            </div>
          ))}
        </div>
      )}

      {/* Edit modal */}
      <Dialog open={!!editing} onOpenChange={(open) => !open && closeEdit()}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-accent flex items-center gap-2">
              <HeartHandshake className="w-5 h-5 text-secondary" />
              Edit Community Partner
            </DialogTitle>
            <DialogDescription className="font-body">
              Changes save instantly. Zip codes decide where this partner shows up when families search — enter as 5-digit codes separated by commas or spaces.
            </DialogDescription>
          </DialogHeader>

          {editing && (
            <div className="space-y-4 py-2">
              <div className="space-y-1.5">
                <Label className="font-body font-semibold text-sm">Organization Name</Label>
                <Input
                  data-testid="cp-input-name"
                  value={editing.name}
                  onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                  placeholder="Meals on Wheels Charlotte"
                />
              </div>

              <div className="space-y-1.5">
                <Label className="font-body font-semibold text-sm">Description</Label>
                <Textarea
                  data-testid="cp-input-description"
                  value={editing.description}
                  onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                  rows={4}
                  placeholder="What this partner offers, who they serve, how families can get involved…"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="font-body font-semibold text-sm">Website</Label>
                  <Input
                    data-testid="cp-input-website"
                    value={editing.website}
                    onChange={(e) => setEditing({ ...editing, website: e.target.value })}
                    placeholder="https://example.org"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="font-body font-semibold text-sm">Phone</Label>
                  <Input
                    data-testid="cp-input-phone"
                    value={editing.phone}
                    onChange={(e) => setEditing({ ...editing, phone: e.target.value })}
                    placeholder="(704) 555-1234"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="font-body font-semibold text-sm">Email</Label>
                  <Input
                    data-testid="cp-input-email"
                    value={editing.email}
                    onChange={(e) => setEditing({ ...editing, email: e.target.value })}
                    placeholder="hello@example.org"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="font-body font-semibold text-sm">Address</Label>
                  <Input
                    data-testid="cp-input-address"
                    value={editing.address}
                    onChange={(e) => setEditing({ ...editing, address: e.target.value })}
                    placeholder="1400 Elm St, Charlotte, NC 28202"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className="font-body font-semibold text-sm flex items-center gap-1.5">
                  <MapPin className="w-4 h-4 text-primary" /> Service Area — Zip Codes
                </Label>

                {/* Nearby zips helper */}
                <div data-testid="cp-zip-helper" className="rounded-xl border-2 border-dashed border-primary/30 bg-primary/5 p-3 mb-2">
                  <div className="flex items-start gap-2 mb-2">
                    <Search className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="font-body font-semibold text-sm text-accent">Nearby zips helper</p>
                      <p className="text-[11px] font-body text-text-secondary leading-snug">
                        Type a city (add the state if it's a common name). We'll fetch every zip in that city so you can add them all with one click.
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Input
                      data-testid="cp-zip-lookup-input"
                      value={zipLookup.query}
                      onChange={(e) => setZipLookup({ ...zipLookup, query: e.target.value })}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); runZipLookup(); } }}
                      placeholder="Charlotte, NC"
                      className="text-sm"
                    />
                    <Button
                      type="button"
                      data-testid="cp-zip-lookup-btn"
                      onClick={runZipLookup}
                      disabled={zipLookup.loading || !zipLookup.query.trim()}
                      className="bg-primary text-white hover:bg-primary-dark rounded-full font-body font-bold flex-shrink-0"
                      size="sm"
                    >
                      {zipLookup.loading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Search className="w-4 h-4 mr-1.5" />}
                      Look up
                    </Button>
                  </div>

                  {zipLookup.results && (
                    <div className="mt-3 bg-white rounded-lg border border-primary/20 p-3">
                      <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                        <p className="text-sm font-body text-text-primary">
                          <strong>{zipLookup.results.count}</strong> zip{zipLookup.results.count === 1 ? "" : "s"} in <strong>{zipLookup.results.city}</strong>
                        </p>
                        <Button
                          type="button"
                          data-testid="cp-zip-lookup-add-all"
                          onClick={() => addZipsToForm(zipLookup.results.zips)}
                          className="bg-secondary text-white hover:bg-[#C96540] rounded-full font-body font-bold"
                          size="sm"
                        >
                          <Plus className="w-3.5 h-3.5 mr-1" /> Add all {zipLookup.results.count}
                        </Button>
                      </div>
                      <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto">
                        {zipLookup.results.zips.map((z) => (
                          <button
                            key={z}
                            type="button"
                            data-testid={`cp-zip-chip-${z}`}
                            onClick={() => addZipsToForm([z])}
                            className="text-xs font-mono px-2 py-0.5 rounded bg-primary/10 text-primary hover:bg-primary hover:text-white transition-colors"
                            title="Click to add this zip"
                          >
                            {z}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <Textarea
                  data-testid="cp-input-zips"
                  value={editing.zip_codes_text}
                  onChange={(e) => setEditing({ ...editing, zip_codes_text: e.target.value })}
                  rows={3}
                  placeholder="28202, 28203, 28204, 28205, 28211"
                  className="font-mono text-sm"
                />
                <p className="text-xs text-text-secondary font-body">
                  Paste as many 5-digit US zips as you want, separated by commas, spaces, or newlines. Only valid 5-digit codes are kept — everything else is stripped.
                </p>
              </div>

              <div className="flex items-center justify-between pt-3 border-t border-[#E2E8F0]">
                <div>
                  <Label className="font-body font-semibold">Listing Active</Label>
                  <p className="text-xs text-text-secondary font-body mt-0.5">Off = kept in your list but hidden from public search results.</p>
                </div>
                <Switch
                  data-testid="cp-input-active"
                  checked={editing.is_active}
                  onCheckedChange={(v) => setEditing({ ...editing, is_active: v })}
                />
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={closeEdit} disabled={saving} data-testid="cp-cancel-btn">Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-primary text-white hover:bg-primary-dark rounded-full font-bold" data-testid="cp-save-btn">
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</> : <><Save className="w-4 h-4 mr-2" /> Save changes</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
